/**
 * Turning a router URL into something safe to report.
 *
 * Two jobs, and both matter as much as each other:
 *  - **Privacy.** The query string is where this app carries set-password and
 *    unlock tokens, the session ticket a lab hands over, and the redirect back
 *    to a lab. None of it may reach Google.
 *  - **Readable reports.** Anything that identifies one record collapses to
 *    `:id`, so a page is one row in a report rather than thousands.
 */
import { MAX_NAME_LENGTH } from './analytics.constants';

/** Segments replaced with `:id`, i.e. everything that identifies one record. */
const ID_LIKE_SEGMENT: readonly RegExp[] = [
  /^[0-9a-f]{24}$/i, // Mongo ObjectId
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, // UUID
  /^\d+$/, // numeric id
  /^[A-Za-z0-9_-]{20,}$/, // opaque token; longer than any route segment we own
];

const MASK = ':id';

function maskSegment(segment: string): string {
  let decoded = segment;
  try {
    decoded = decodeURIComponent(segment);
  } catch {
    // Malformed escape sequence; judge the raw segment instead.
  }

  if (decoded.includes('@') || decoded.length > MAX_NAME_LENGTH) return MASK;
  return ID_LIKE_SEGMENT.some((pattern) => pattern.test(decoded)) ? MASK : decoded;
}

function keepAllowedQuery(
  rawQuery: string | undefined,
  allowedQueryParams: readonly string[],
): string {
  if (!rawQuery || allowedQueryParams.length === 0) return '';

  return rawQuery
    .split('&')
    .filter((pair) => allowedQueryParams.includes(pair.split('=')[0]))
    .join('&');
}

/**
 * Strip a URL down to path only, with id-like segments masked and the query
 * dropped except for explicitly allowed keys.
 *
 * Pure by design — the allowlist is passed in rather than read from module
 * state, so this is safe to reuse anywhere a URL is about to be logged.
 */
export function sanitizeAnalyticsPath(
  url: string,
  allowedQueryParams: readonly string[] = [],
): string {
  if (!url) return '/';

  const [beforeHash] = url.split('#');
  const [rawPath, rawQuery] = beforeHash.split('?');

  const path = '/' + rawPath.split('/').filter(Boolean).map(maskSegment).join('/');
  const query = keepAllowedQuery(rawQuery, allowedQueryParams);

  return query ? `${path}?${query}` : path;
}

/**
 * An absolute URL reduced to origin plus sanitized path, or undefined when it
 * is not a URL at all. Used for the referrer, which arrives from the browser
 * rather than from the router — a lab with a loose referrer policy would
 * otherwise hand over its own query string.
 */
export function sanitizeAbsoluteUrl(url: string): string | undefined {
  if (!url) return undefined;
  try {
    const parsed = new URL(url);
    return `${parsed.origin}${sanitizeAnalyticsPath(parsed.pathname)}`;
  } catch {
    return undefined;
  }
}
